import {
  SettingsSync,
  TradingStreamClient,
  WebPlatform,
  api,
  type CommandSender,
  type StreamEvent,
  bindTradingState,
  resetTrading,
  effectiveVisualStyle,
  colorSchemeOf,
  accentColorOf,
  applyMoodAttributes,
  freshSecurityNotice,
  securityNotice,
  SECURITY_NOTICE_DOC_TYPE,
  BROKER_VERIFICATION_DOC,
  COACH_DOC_ID,
  FIRM_RULES_DOC,
  type Settings,
} from "@helm/core";
import { pushToast } from "./components/Toasts";
import { loadAccountRisk, riskEntry } from "./risk/riskSettings";
import { verificationDocChanged } from "./risk/brokerVerification";
import { firmRulesDocChanged } from "./risk/firmGate";
import { layoutDocChanged } from "./workspace/workspaceStore";
import { bracketDocChanged } from "./brackets/bracketStore";
import { drawingsDocChanged } from "./chart/drawings/drawingsStore";
import { watchlistDocChanged } from "./sidebar/watchlistStore";
import { coachDocChanged } from "./coach/coachGate";
import { sidePalettes, allSchemes, schemeTokens } from "@helm/design";

declare const __HELM_VERSION__: string;

let client: TradingStreamClient | null = null;
let unbindTrading: (() => void) | null = null;
let unwatchResume: (() => void) | null = null;

/**
 * Apply visual settings. Replaces core's default applier so the side palettes come from
 * @helm/design (Tradesea greenRed): the default palette clears the inline overrides so each
 * theme's own --buy/--sell (dark vs light) applies; the others override the side tokens.
 */
export function applyVisualSettings(s: Settings, doc: Document = document): void {
  const root = doc.documentElement;
  root.dataset["theme"] = s.theme;
  // Visual style (Tradovate default / Tradesea / Glacis): tokens.css switches colour tables and
  // density. A stored "tradesea" that was never chosen reads as Tradovate (effectiveVisualStyle).
  root.dataset["style"] = effectiveVisualStyle(s);
  const scheme = colorSchemeOf(s.color_scheme);
  root.dataset["scheme"] = scheme;
  // Mood flair (moods.css, chrome only): present only with a mood scheme; Calm stops its motion.
  applyMoodAttributes(s, root.dataset);
  // Inline colour tokens intentionally override only colours from compatibility styles.
  // Existing chart/popout observers already track the style attribute.
  for (const key of Object.keys(schemeTokens("midnight"))) root.style.removeProperty(`--${key}`);
  root.style.removeProperty("color-scheme");
  if (scheme !== "legacy") {
    root.dataset["theme"] = allSchemes[scheme].mode;
    root.style.colorScheme = allSchemes[scheme].mode;
    const side =
      s.sidePalette === "blueRed" || s.sidePalette === "colorBlindSafe" ? s.sidePalette : "greenRed";
    for (const [key, value] of Object.entries(schemeTokens(scheme, side, accentColorOf(s.accent_color))))
      root.style.setProperty(`--${key}`, value);
    return;
  }

  const vars = [
    "--buy",
    "--buy-text",
    "--buy-bg",
    "--buy-weak",
    "--sell",
    "--sell-text",
    "--sell-bg",
    "--sell-weak",
  ];
  if (s.sidePalette === "greenRed") {
    for (const v of vars) root.style.removeProperty(v);
    return;
  }
  const p = sidePalettes[s.sidePalette];
  const weak = (hex: string): string => `color-mix(in srgb, ${hex} 16%, transparent)`;
  const solid = (hex: string): string => `color-mix(in srgb, ${hex} 72%, black)`;
  root.style.setProperty("--buy", p.buy);
  root.style.setProperty("--buy-text", p.buy);
  root.style.setProperty("--buy-bg", solid(p.buy));
  root.style.setProperty("--buy-weak", weak(p.buy));
  root.style.setProperty("--sell", p.sell);
  root.style.setProperty("--sell-text", p.sell);
  root.style.setProperty("--sell-bg", solid(p.sell));
  root.style.setProperty("--sell-weak", weak(p.sell));
}

/** One settings synchroniser per window; DocChanged frames trigger a refresh. */
export const settingsSync = new SettingsSync(undefined, applyVisualSettings);

function tabId(): string {
  const key = "helm:tab-id";
  try {
    const existing = sessionStorage.getItem(key);
    if (existing) return existing;
    const id = crypto.randomUUID();
    sessionStorage.setItem(key, id);
    return id;
  } catch {
    return crypto.randomUUID();
  }
}

/**
 * The window's trading socket as a command sender (trading/control.ts), or null before sign-in.
 * Commands never open a socket of their own and never queue: no socket, nothing sent.
 */
export function tradingSender(): CommandSender | null {
  return client;
}

/** Security notices already handled in this page (the stream replays its ring on reconnect). */
const seenNotices = new Set<string>();

/** Listeners for security notices (a PIN sign-in or lockout on this account, ADR-0035). */
const securityListeners = new Set<(kind: string) => void>();

/** Observe security notices, e.g. to refresh Settings → Devices; returns the unsubscribe. */
export function onSecurityNotice(l: (kind: string) => void): () => void {
  securityListeners.add(l);
  return () => {
    securityListeners.delete(l);
  };
}

/** Listeners for every trading-stream frame (order feedback, notices), kept across reconnects. */
const frameListeners = new Set<(env: StreamEvent) => void>();

/** Observe trading-stream frames; returns the unsubscribe. */
export function onTradingFrame(l: (env: StreamEvent) => void): () => void {
  frameListeners.add(l);
  return () => {
    frameListeners.delete(l);
  };
}

/** Start (or return) the single trading-stream client for this window. */
export function tradingStream(): TradingStreamClient {
  if (client) return client;
  const proto = location.protocol === "https:" ? "wss:" : "ws:";
  client = new TradingStreamClient({
    baseUrl: `${proto}//${location.host}`,
    getToken: async () => (await api.gatewayToken()).token,
    clientVersion: typeof __HELM_VERSION__ === "string" ? __HELM_VERSION__ : "dev",
    tabId: tabId(),
  });
  client.subscribe((env) => {
    for (const l of frameListeners) l(env);
    if (env.body.case === "docChanged" && env.body.value.docType === "settings") {
      // settings/coach is the Discipline coach's own document; everything else is settings/main.
      if (env.body.value.docId === COACH_DOC_ID) coachDocChanged(Number(env.body.value.version));
      else void settingsSync.refresh(Number(env.body.value.version));
    }
    // Workspaces (layout/desktop) saved on another device.
    if (env.body.case === "docChanged" && env.body.value.docType === "layout") {
      layoutDocChanged(env.body.value.docId, Number(env.body.value.version));
    }
    // Bracket strategies (bracket_presets/main) saved on another device.
    if (env.body.case === "docChanged" && env.body.value.docType === "bracket_presets") {
      bracketDocChanged(env.body.value.docId, Number(env.body.value.version));
    }
    // Drawings of a symbol root (drawings/{root}) saved on another device or window.
    if (env.body.case === "docChanged" && env.body.value.docType === "drawings") {
      drawingsDocChanged(env.body.value.docId, Number(env.body.value.version));
    }
    // Watchlists (watchlist/main) saved on another device.
    if (env.body.case === "docChanged" && env.body.value.docType === "watchlist") {
      watchlistDocChanged(env.body.value.docId, Number(env.body.value.version));
    }
    // A PIN sign-in or a PIN lockout on this account (ADR-0035): tell every signed-in device.
    if (
      env.body.case === "docChanged" &&
      env.body.value.docType === SECURITY_NOTICE_DOC_TYPE &&
      freshSecurityNotice(env.body.value.docId, Number(env.body.value.version), Date.now(), seenNotices)
    ) {
      const t = securityNotice(env.body.value.docId);
      if (t) pushToast(t, { assertive: t.tone === "danger", ttlMs: 15_000 });
      for (const l of securityListeners) l(env.body.value.docId);
    }
    // Another device recorded or withdrew an R|Trader Pro match (ADR-0045).
    if (env.body.case === "docChanged" && env.body.value.docType === BROKER_VERIFICATION_DOC) {
      verificationDocChanged(env.body.value.docId, Number(env.body.value.version));
    }
    // Another device saved an account's firm rules (ADR-0047).
    if (env.body.case === "docChanged" && env.body.value.docType === FIRM_RULES_DOC) {
      firmRulesDocChanged(env.body.value.docId, Number(env.body.value.version));
    }
    // Another device saved an account's risk settings: refetch it if this window has it.
    if (env.body.case === "docChanged" && env.body.value.docType === "risk_settings") {
      const { docId, version } = env.body.value;
      const cur = riskEntry(docId);
      if (cur.loaded && Number(version) > cur.version) void loadAccountRisk(docId);
    }
  });
  // Orders, fills, positions, accounts, session and risk state (packages/core trading/).
  unbindTrading = bindTradingState(client);
  // CB4: a phone waking up, a tab shown again or the network coming back may hold a half-open
  // socket that still looks connected: probe it now and resync (or reconnect at once).
  const c = client;
  unwatchResume = new WebPlatform().onResume(() => {
    c.checkLiveness();
  });
  client.start();
  return client;
}

export function stopTradingStream(): void {
  client?.stop();
  client = null;
  unbindTrading?.();
  unbindTrading = null;
  unwatchResume?.();
  unwatchResume = null;
  resetTrading();
}
